import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import FormLayoutDrawer from 'src/app/layout/form-layout/form-layout-drawer'
import { useCreateCreditNote, useDeleteDraft, useUndoDocument, useUpsertDocument } from '../checkout-document-form.api'
import useCheckoutDocumentForm from '../checkout-document-form.hook'
import { CheckoutDocumentFormSchema, DocumentDetailsSchema } from '../checkout-document-form.schema'
import { getDocumentDefaultCustomer, getReferenceCustomers, getUpsertDocumentInput } from '../checkout-document-form.utils'
import { LayoutLoadingError } from './layout-loading-error'
import { LayoutToolbar } from './layout-toolbar'
import { CoQuery } from './co-query-wrapper'
import { Checkbox, Divider, FormControlLabel, Grid, MenuItem, Select, TextField, Typography, Tabs, Tab, Box } from '@vapor/material'
import { Controller } from 'react-hook-form'
import CoAmounts from './co-amounts'
import CheckoutFiscalTypeInput from './co-fiscal-type'
import { FormItem, HICLoading, useSyncConfirm } from 'hic-components'
import { useTranslation } from 'react-i18next'
import { CashDeskSimpleSelector } from '../../../../../components/cash-desk-input/cash-desk-simple-selector'
import CoCustomer from './co-customer'
import { customerFragmentToSchema } from '../../../../../components/document-customer-input/document-customer-input'
import PaymentsFormField from '../../components/payments/payments-form-field'
import CoDetails, { getDocumentDetailsFromProduction } from './co-details'
import { useSettings, useTenant } from 'src/app/hooks/context/context.hook'
import {
  CashDeskFragment,
  CheckoutDocumentGroupType,
  CoDocumentDetailFragment,
  CoDocumentFragment,
  EmailState,
  ManualDocumentDetailInput,
  ManualDocumentInput,
  PaymentTypeCode,
  useCashDesksQuery,
  useCreateLinkForUnpaidDocumentMutation,
  usePaymentActivesQuery,
  useSendPaymentLinkEMailForUnpaidDocumentsMutation,
} from 'src/api/api.generated'
import CoCreditNoteHeader from './co-credit-note-header'
import { CoDeposits } from './deposit'
import CoReferenceDocumentHeader from './co-reference-document-header'
import DocumentReportViewer from '../../../../../components/report-viewer/documnent/document-report-viewer'
import calculateCoTaxes, { calculateFiscalAdvanceAmount } from './co-taxes'
import { PaidInFullDocumentsGrid } from './paid-in-full/paid-in-full-documents-grid'
import { ApolloError } from '@apollo/client'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { DocumentEmailsForm } from './document-emails/document-emails.drawer'
import { sendWhatsappMessage } from 'src/utils/whatsapp-utils'
import { useScelNoteHook } from './co-receipt-note-hook'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { GatewayPaymentType, TsPayInfo } from '../../components/pay-by-gateway/pay-by-gateway.types'
import { PayByGateway } from '../../components/pay-by-gateway/pay-by-gateway'
import { ResourceType } from './co-form-layout.types'
import { formatResource } from 'src/utils/resource-type'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import SendCommunicationEmail from 'src/app/components/send-communication-email/send-communication-email'
import { v4 as uuidv4 } from 'uuid'
import { DepositSplitDataWrapper } from './deposit'
export type CoFormLayoutProps = {
  propertyId: string
  open: boolean
  onClose: () => void
  queryData: CoQuery
  onDocumentUpserted: (documentId: string, closedBillsIds?: string[]) => void
  onCreditNoteCreated: (creditNoteId: string, manualDocumentId?: string) => void
  onOpenReferenceDocument?: (documentId: string) => void
  refetchQueries?: string[]
  createReceiptZero?: boolean
}

export default function CoFormLayout({ propertyId, open, onClose, queryData, onDocumentUpserted, refetchQueries, onCreditNoteCreated, onOpenReferenceDocument, createReceiptZero }: CoFormLayoutProps) {
  const { t } = useTranslation()
  const formatDate = useFormatDate()
  const [loading, setLoading] = useState(false)
  const showError = useErrorSnackBar()
  const showSuccess = useSuccessSnackBar()
  const { upsertCoDocument: upsertDocument, loading: upsertLoading } = useUpsertDocument(propertyId, refetchQueries)
  const deleteDocument = useDeleteDraft(refetchQueries)
  const undoDocument = useUndoDocument(refetchQueries)
  const createCreditNote = useCreateCreditNote(refetchQueries)
  const [sendPaymentLinkEmailForUnpaidDocument] = useSendPaymentLinkEMailForUnpaidDocumentsMutation()
  const [createLinkForUnpaidDocument] = useCreateLinkForUnpaidDocumentMutation()
  const confirm = useSyncConfirm()
  const accounting = useSettings()?.accounting || null
  const { id: tenantId, currency } = useTenant()
  const { compileScelNote } = useScelNoteHook()
  const [openDocumentViewer, setOpenDocumentViewer] = useState(false)
  const [openSendDocumentByEmail, setOpenSendDocumentByEmail] = useState(false)
  const [customerIsTouched, setCustomerAsTouched] = useState(false)
  const [detailsIsTouched, setDetailsAsTouched] = useState(false)
  const [paymentsIsTouched, setPaymenIsTouched] = useState(false)
  const localizeString = useLocalizeString()
  const [activeTab, setActiveTab] = useState<number>(0)

  const { data: cashDesksQuery, refetch: cashDesksQueryRefetch } = useCashDesksQuery({ skip: true })

  //#region functions
  const { data: paymentActives } = usePaymentActivesQuery({
    variables: {
      tenantId: tenantId,
      propertyId: propertyId,
    },
  })

  const unpaidPaymentActives = useMemo(() => {
    return paymentActives?.paymentActives?.filter((pa) => pa.paymentType.code === PaymentTypeCode.UNPAID).map((i) => i.id)
  }, [paymentActives])

  const [openDocumentEmails, setOpenDocumentEmails] = useState(false)

  const paymentDetailsWithUUID = useMemo(() => {
    if (queryData.mode === 'UPDATE' || queryData.mode === 'READ_ONLY') {
      return queryData.document.documentPaymentDetails.map((i) => ({ uuid: uuidv4(), ...i }))
    }
  }, [queryData])

  const onSubmit: (data: CheckoutDocumentFormSchema, draft: boolean, finalized: boolean, updatePaymensDetailsOnly: boolean) => Promise<void> = useCallback(
    async (data, draft, finalized, updatePaymensDetailsOnly) => {
      const input = getUpsertDocumentInput({ queryData, draft, finalized, updatePaymensDetailsOnly, data, t, paymentDetailsWithUUID, createReceiptZero })
      if (input === null) {
        return
      }
      setLoading(true)
      const result = await upsertDocument(input)
      if (result.type === 'SUCCESS') {
        onDocumentUpserted(result.documentId, result.closedBillsIds)
        if (finalized) {
          showSuccess(t('pms.common.checkout_document.finalized_document_saved'))
          setDetailsAsTouched(false)
        }
        onClose()
      } else {
        showError(result.error)
      }
      setLoading(false)
    },
    [upsertDocument, queryData, onClose, paymentDetailsWithUUID, t, createReceiptZero],
  )

  const handleDelete = useCallback(async () => {
    if (queryData.mode === 'UPDATE') {
      const documentId = queryData.document?.id
      if (documentId) {
        setLoading(true)
        const result = await deleteDocument(documentId)
        if (result.type === 'SUCCESS') {
          onClose()
        } else {
          showError(result.error)
        }
        setLoading(false)
      }
    }
  }, [queryData])

  const { handleSubmit, control, watch, setValue, getValues, errors } = useCheckoutDocumentForm(
    onSubmit,
    {
      fiscalType: createReceiptZero ? 'RECEIPT' : accounting?.coDocumentType,
      addNoteToZeroReceipt: createReceiptZero ?? false,
      payments: createReceiptZero ? [] : undefined,
      deposits: createReceiptZero ? [] : undefined,
    },
    unpaidPaymentActives,
  )
  const formValue = watch()

  const [syncData, setSyncData] = useState(false)

  const allowChangesOnFinalizedDocument = useMemo(() => {
    let check = false
    if (queryData.mode === 'READ_ONLY') {
      const felInfo = queryData.document.felAdditionalInfo
      check = queryData.document.fiscalType === 'INVOICE' && (felInfo === null || !felInfo?.sent || felInfo.state === 'SCARTATO' || felInfo.state === 'RIFIUTATO')
    }
    return check
  }, [queryData])

  const referenceCustomers = useMemo(() => {
    if (queryData.mode !== 'LOADING' && queryData.mode !== 'ERROR') {
      return queryData.mode === 'CREATE' ? getReferenceCustomers(queryData.reservations, undefined, queryData.bills) : getReferenceCustomers(queryData.reservations, queryData.document)
    }
  }, [queryData, syncData, createReceiptZero])

  const handleClose = useCallback(async () => {
    if (allowChangesOnFinalizedDocument && (detailsIsTouched || customerIsTouched)) {
      const messages: string[] = []
      if (detailsIsTouched) messages.push(t('pms.accounting.documents.unsaved_details_rows_changes'))
      if (customerIsTouched) messages.push(t('pms.accounting.documents.unsaved_customer_changes'))
      const response = await confirm({
        title: t('commons.unsaved_changes'),
        message: messages.join('\n'),
        confirmButtonText: t('commons.close_and_discard_changes'),
        cancelButtonText: t('commons.cancel'),
        confirmIsDanger: true,
      })
      if (!response) return
      onClose()
    } else {
      onClose()
    }
  }, [allowChangesOnFinalizedDocument, detailsIsTouched, customerIsTouched, onClose])

  const title = useMemo(() => {
    if (createReceiptZero) {
      return t('pms.common.checkout_document.new_zero_receipt')
    } else if (queryData.mode === 'UPDATE') {
      return t('pms.common.checkout_document.title_update', { documentNumber: queryData.document.numberExtended })
    } else if (queryData.mode === 'READ_ONLY') {
      return t('pms.common.checkout_document.title_readonly', { documentNumber: queryData.document.numberExtended })
    } else if (queryData.mode === 'CREATE') {
      return t('pms.common.checkout_document.title_create')
    } else {
      return ''
    }
  }, [t, queryData.mode, createReceiptZero])

  const documentDate = useMemo(() => {
    if (queryData.mode === 'UPDATE' || queryData.mode === 'READ_ONLY') {
      return <Typography variant="body" sx={{ pt: 1, pl: 2 }}>{`${t('pms.commons.document.date')}: ${formatDate(queryData.document.documentDate)}`}</Typography>
    } else {
      return <></>
    }
  }, [t, queryData])

  const [mounted, setMounted] = useState(true)

  useEffect(() => {
    // Caso di aggiornamento del customer corrente in seguito a sincronizzazione
    if (watch('customer')?.id) {
      if (queryData.mode === 'CREATE') {
        const defC = getDocumentDefaultCustomer(queryData)
        if (watch('customer')?.id === defC.id) {
          setValue('customer', defC)
        }
      } else if (queryData.mode === 'UPDATE' || queryData.mode === 'READ_ONLY') {
        const consolidatedData = queryData.document.consolidatedData
        const updatedCustomer = customerFragmentToSchema(
          consolidatedData ? { ...consolidatedData.customer, id: queryData.document.customerId } : { ...queryData.document.customer, id: queryData.document.customerId },
        )
        if (watch('customer')?.id === updatedCustomer.id) {
          setValue('customer', updatedCustomer)
          setSyncData(true)
        }
      }
    } else {
      if (queryData.mode === 'CREATE') {
        const defC = getDocumentDefaultCustomer(queryData)
        setValue('customer', defC)
      }

      if (mounted) {
        if ((queryData.mode === 'UPDATE' || queryData.mode === 'READ_ONLY') && paymentDetailsWithUUID) {
          const consolidatedData = queryData.document.consolidatedData
          setValue(
            'customer',
            customerFragmentToSchema(consolidatedData ? { ...consolidatedData.customer, id: queryData.document.customerId } : { ...queryData.document.customer, id: queryData.document.customerId }),
          )
          setValue('fiscalType', queryData.document.fiscalType)

          const payments = paymentDetailsWithUUID
            .filter((i) => i.referenceDocument?.documentType !== 'ADVANCE')
            .map((d) => {
              return {
                uuid: d.uuid,
                paymentActiveId: d.paymentActiveId,
                amount: d.amount,
                editable: true,
                expireDate: d.expireDate || undefined,
              }
            })
          setValue('payments', payments)
          setValue('cashDeskId', queryData.document.cashDeskId)
        }
      }
      return () => {
        setMounted(false)
      }
    }
  }, [queryData, paymentDetailsWithUUID])

  const customerCurrentData = useMemo(() => {
    if (queryData.mode === 'UPDATE' || queryData.mode === 'READ_ONLY') {
      return queryData.document.customer
    }
  }, [mounted, queryData])

  useEffect(() => {
    const splitPayment = paymentActives?.paymentActives.find((pa) => pa.paymentType.code === PaymentTypeCode.SPLITPAYMENT)
    const prevPayments = getValues('payments') ?? []

    const hasSelectedInvoice = watch('fiscalType') === 'INVOICE'
    const shouldAddSplitPaymentRow = hasSelectedInvoice && formValue?.customer?.splitPayment && splitPayment && prevPayments.findIndex((p) => p.paymentActiveId === splitPayment.id) < 0
    const shouldRemoveSplitPaymentRow =
      (!hasSelectedInvoice || formValue?.customer?.splitPayment === false) && splitPayment && prevPayments.findIndex((p) => p.paymentActiveId === splitPayment.id) >= 0

    //#region taxes amount calculation

    const fiscalDepositsAmount = calculateFiscalAdvanceAmount(watch('deposits') || []) // key is vatPercentage, value is the amount with the vatPercentage

    const taxesAmount = (() => {
      let documentInfo: CoDocumentDetailFragment[] = []

      if (queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE') {
        documentInfo = queryData.document.documentDetails ?? []
      }

      if (queryData.mode === 'CREATE') {
        documentInfo = getDocumentDetailsFromProduction(queryData.bills)
      }

      const nonFiscalDepositsAmount = Math.floor(
        watch('deposits')?.reduce((prev, curr) => {
          if (curr.fiscalType !== 'NON_FISCAL') return prev
          return prev + (curr?.usedAmount ?? 0)
        }, 0) ?? 0,
      )

      const billTaxes = calculateCoTaxes(documentInfo, fiscalDepositsAmount, nonFiscalDepositsAmount)

      return Math.round(billTaxes) // nonFiscalDepositsTaxes
    })()
    //#endregion

    if (shouldAddSplitPaymentRow && taxesAmount > 0) {
      //#region remove taxes amount from other payment rows
      let amountToRemove = taxesAmount

      const otherRows = prevPayments
        .map((row) => {
          if (row.amount < amountToRemove) {
            amountToRemove = amountToRemove - row.amount
            return {
              ...row,
              amount: 0,
            }
          } else {
            const updatedRow = {
              ...row,
              amount: row.amount - amountToRemove,
            }
            amountToRemove = 0
            return updatedRow
          }
        })
        .filter((r) => r.amount > 0)
      //#endregion

      const updPayments = [{ uuid: uuidv4(), amount: taxesAmount, paymentActiveId: splitPayment.id, editable: false }, ...otherRows]

      setValue('payments', updPayments)
      setValue('splitPayment', true)
    } else if (shouldRemoveSplitPaymentRow) {
      let taxesAmountAdded = false
      const newRows = prevPayments.map((row) => {
        if (!row.paymentActiveId && !taxesAmountAdded) {
          taxesAmountAdded = true
          return {
            ...row,
            editable: true,
            amount: row.amount + taxesAmount,
          }
        }
        return {
          ...row,
          editable: true,
        }
      })

      if (!taxesAmountAdded) {
        newRows.push({
          uuid: uuidv4(),
          paymentActiveId: undefined,
          amount: taxesAmount,
          editable: true,
          expireDate: undefined,
        })
      }

      setValue(
        'payments',
        newRows.filter((p) => p.paymentActiveId !== splitPayment.id),
      )
      setValue('splitPayment', false)
    }
  }, [watch('customer.splitPayment'), watch('payments'), watch('fiscalType'), JSON.stringify(watch('deposits')), paymentActives, formValue.deposits])

  /** Update scelNote */
  useEffect(() => {
    const customerName = watch('customer.name')
    const isReceipt = watch('fiscalType') === 'RECEIPT'
    if (isReceipt && customerName && queryData.mode === 'CREATE' && queryData.reservations.length) {
      const { reservations } = queryData
      const reservation = reservations.length === 1 ? reservations[0] : undefined
      const reservedResource = reservation?.reservedResources?.length === 1 ? reservation.reservedResources[0] : undefined
      const resourceOccupancy = reservedResource?.resourceOccupancies?.length === 1 ? reservedResource?.resourceOccupancies[0] : undefined
      const resource = resourceOccupancy ? formatResource(localizeString, resourceOccupancy.reservedResourceType, resourceOccupancy.resource) : undefined
      const scelNote = compileScelNote({
        customer: { name: customerName },
        resource: {
          code: resource,
        },
        reservation: {
          reservationReference: {
            mainGuest: {
              name: reservation?.reference.mainGuest.name,
            },
            voucher: reservation?.voucher ?? undefined,
          },
        },
        reservedResource: {
          start: reservedResource ? formatDate(reservedResource.start) : undefined,
          end: reservedResource ? formatDate(reservedResource.end) : undefined,
        },
      })
      if (scelNote) setValue('scelNote', scelNote)
    }
  }, [JSON.stringify(watch('customer.name')), JSON.stringify(watch('fiscalType')), JSON.stringify(queryData)])

  useEffect(() => {
    if ((queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE') && (queryData.document.fiscalType === 'INVOICE' || queryData.document.fiscalType === 'RECEIPT') && queryData.document.note) {
      setValue('note', queryData.document.note)
    }
  }, [JSON.stringify(queryData)])

  useEffect(() => {
    if (queryData.mode === 'CREATE') {
      const savedGroupBy = window.localStorage.getItem('createDocumentGroupBy') as CheckoutDocumentGroupType
      if (savedGroupBy && savedGroupBy in CheckoutDocumentGroupType) {
        setValue('groupBy', savedGroupBy)
      } else {
        setValue('groupBy', 'ANALITIC')
      }
    }
  }, [setValue, queryData.mode])

  const [cashDesksOpened, setCashDesksOpened] = useState<CashDeskFragment[]>([])

  useEffect(() => {
    const fetchCashDesks = async () => {
      const cashDeskQueryResult = await cashDesksQueryRefetch({ tenantId, propertyId, open: true })
      if (!cashDeskQueryResult.error && cashDeskQueryResult.data && cashDeskQueryResult.data.cashDesks) {
        setCashDesksOpened(cashDeskQueryResult.data.cashDesks)
      }
    }
    if (tenantId && propertyId) fetchCashDesks()
  }, [tenantId, propertyId])

  // const defaultDocumentTypeForCustomer = useCallback(
  //   (customerId: string): CheckoutFiscalType | null => {
  //     if (customerId && queryData.mode === 'CREATE' && queryData.reservations) {
  //       const refCustomers = getReferenceCustomers(queryData.reservations)
  //       if (refCustomers && refCustomers.length > 0) {
  //         switch (refCustomers.find((c) => c.id === customerId)?.type) {
  //           case 'COMPANY':
  //             return 'INVOICE'
  //           case 'PERSON':
  //             return 'RECEIPT'
  //         }
  //       }
  //     }
  //     return null
  //   },
  //   [queryData.mode, setValue],
  // )

  const [dataForGatewayPayment, setDataForGatewayPayment] = useState<GatewayPaymentType>()

  const handleUndo = useCallback(async () => {
    if (queryData.mode === 'READ_ONLY') {
      const documentId = queryData.document?.id
      if (documentId) {
        const c = await confirm({
          title: t('pms.common.checkout_document.confirm_title'),
          message: t('pms.common.checkout_document.confirm_message_undo_document'),
          confirmButtonText: t('commons.ok'),
          cancelButtonText: t('commons.cancel'),
        })
        if (!c) return
        setLoading(true)
        const result = await undoDocument(documentId)
        setLoading(false)
        if (result.type === 'FAIL') {
          showError(result.error)
        }
      }
    }
  }, [queryData])

  const handleCreateCreditNote = useCallback(
    async (manualDocument: boolean) => {
      if (queryData.mode !== 'READ_ONLY') return
      const document = queryData.document
      if (!document) return

      const dialogMessage =
        document.fiscalType === 'RECEIPT' || document.fiscalType === 'NON_FISCAL'
          ? t('pms.common.checkout_document.confirm_message_credit_note_reso')
          : manualDocument
          ? t('pms.common.checkout_document.confirm_message_credit_note_manual_document')
          : t('pms.common.checkout_document.confirm_message_credit_note')

      const c = await confirm({
        title: t('pms.common.checkout_document.confirm_title'),
        message: dialogMessage,
        confirmButtonText: t('commons.ok'),
        cancelButtonText: t('commons.cancel'),
      })
      if (!c) return

      let manualDocumentInput: ManualDocumentInput | undefined = undefined
      if (manualDocument) {
        const documentDetailInputs: ManualDocumentDetailInput[] = document.documentDetails
          .filter((i) => i.quantity > 0) // This filter excludes 'Advance' rows (fix issue reported on ticket HIC-1850)
          .map((dd) => ({
            amount: dd.amount,
            date: dd.date,
            description: dd.description,
            quantity: dd.quantity,
            vatId: dd.vat?.id,
            reference: dd.reference,
          }))

        const cashDeskIdForManualDocument = cashDesksOpened?.find((c) => c.cashDeskType.id === document.cashDesk.cashDeskTypeId)

        if (!cashDeskIdForManualDocument || !cashDeskIdForManualDocument.id) {
          showError(t('pms.common.checkout_document.unable_to_find_cash_desk'))
          return
        }

        manualDocumentInput = {
          tenantId,
          propertyId,
          currency,
          cashDeskId: cashDeskIdForManualDocument.id, // Use a cash desk of same type as the original document (fix required by ticket hic-1850)
          customerId: document.customerId,
          fiscalType: document.fiscalType,
          documentDetails: documentDetailInputs,
        }
      }
      setLoading(true)
      const result = await createCreditNote(document.id, document.documentType, manualDocumentInput)
      setLoading(false)
      if (result.type !== 'SUCCESS') {
        showError(result.error)
      }
      if (result.type !== 'FAIL') {
        const manualDocumentId = result.type === 'SUCCESS' ? result.manualDocumentId : undefined
        onCreditNoteCreated(result.creditNoteId, manualDocumentId)
      }
    },
    [queryData, currency, cashDesksOpened],
  )

  const readOnlyDocument = useMemo(() => {
    if (queryData.mode === 'READ_ONLY') {
      return queryData.document
    }
    return undefined
  }, [queryData])

  const readonlyFiscalType = useMemo(() => {
    return (
      queryData.mode === 'READ_ONLY' ||
      (queryData.mode === 'UPDATE' &&
        (queryData.document.fiscalType === 'NON_FISCAL' ||
          queryData.document.fiscalType === 'PAID_IN_FULL' ||
          queryData.document.fiscalType === 'CREDIT_NOTE' ||
          queryData.document.fiscalType === 'RETURN_DEPOSIT'))
    )
  }, [queryData])

  // This is a list of all resources related to reservations related to this document (contains all resources not only referenced to production)
  const resourceReferences = useMemo(() => {
    const result = new Map<string, { resourceType: ResourceType; resourceCode: string }>()
    if (queryData.mode === 'CREATE') {
      queryData.reservations.forEach((reservation) => {
        reservation.reservedResources?.forEach((reservedResource) => {
          reservedResource.resourceOccupancies.forEach(({ resource, reservedResourceType }) => {
            if (resource) result.set(resource.id, { resourceType: reservedResourceType, resourceCode: resource.code })
          })
        })
      })
    }
    return result
  }, [queryData])

  /**
   * The list of resourceTypes referenced from production assigned used in this document
   */
  const documentResourceTypes = useMemo(() => {
    const resultResourceTypes: string[] = []
    // contains resource information relative to produciont included in this document
    // const resultResources = Map<string, {}
    if (queryData.mode === 'CREATE') {
      queryData.bills.forEach((b) => {
        b.productions?.forEach((p) => {
          p.production.reservedResource?.resourceOccupancies.forEach((ro) => {
            ro.reservedResourceTypeId
            const rt = ro.reservedResourceTypeId
            if (!resultResourceTypes.includes(rt)) resultResourceTypes.push(rt)
          })
        })
      })
    }
    return resultResourceTypes
  }, [queryData])

  const tsPayPaymentActiveIds = useMemo(() => {
    return dataForGatewayPayment?.paymentActivesWithGateway?.paymentActives?.filter((pa) => pa.paymentMethods?.some((m) => m.gateway?.type === 'TSPAY')).map((pa) => pa.id)
  }, [dataForGatewayPayment])

  const sendEmailForPaidInFullDocument = async (
    documentId: string,
    tenantId: string,
    propertyId: string,
  ): Promise<{ type: 'SUCCESS'; data: EmailState | undefined } | { type: 'ERROR'; error: ApolloError | undefined }> => {
    try {
      const res = await sendPaymentLinkEmailForUnpaidDocument({
        variables: {
          input: {
            tenantId: tenantId,
            propertyId: propertyId,
            paidInFullDocumentId: documentId,
          },
        },
      })
      if (res.data) return { type: 'SUCCESS', data: res.data.sendPaymentLinkEmailForUnpaidDocuments?.emailState }
    } catch (error) {
      return { type: 'ERROR', error: error as ApolloError }
    }
    return { type: 'ERROR', error: undefined }
  }

  const handleSendPaidInFullEmail = useCallback(async () => {
    if (queryData.mode === 'UPDATE') {
      setLoading(true)
      const result = await sendEmailForPaidInFullDocument(queryData.document.id, tenantId, propertyId)

      if (result?.type === 'SUCCESS') {
        showSuccess(t('pms.payment.tsPay.link2pay.email_sent'))
      } else {
        showError(result?.error ?? t('commons.generic_error_occurred'))
      }
      setLoading(false)
    }
    return undefined
  }, [queryData, sendPaymentLinkEmailForUnpaidDocument, sendEmailForPaidInFullDocument])

  const generateLink = async (
    tenantId: string,
    propertyId: string,
    document: CoDocumentFragment,
  ): Promise<{ type: 'SUCCESS'; url: string | undefined } | { type: 'ERROR'; error: ApolloError | undefined }> => {
    try {
      const res = await createLinkForUnpaidDocument({
        variables: {
          input: {
            tenantId: tenantId,
            propertyId: propertyId,
            documentId: document.id,
            customerId: document.customerId,
            payment: { amount: document.totalAmount, currency: document.currency, description: document.numberExtended },
          },
        },
      })
      if (res.data) return { type: 'SUCCESS', url: res.data.createLinkForUnpaidDocument.url }
    } catch (error) {
      return { type: 'ERROR', error: error as ApolloError }
    }
    return { type: 'ERROR', error: undefined }
  }

  const handleSendPaidInFullMessage = useCallback(async () => {
    if (queryData.mode === 'UPDATE') {
      const document = queryData.document
      const phoneNumber = document.customer.phoneContacts?.filter((n) => n.isDefault)?.[0].value
      if (document && phoneNumber) {
        const response = await generateLink(tenantId, propertyId, document)
        if (response.type === 'SUCCESS' && response.url) {
          const message = t('pms.commons.document.whatsapp_message_with_link', { link: response.url })
          sendWhatsappMessage(phoneNumber, message)
        }
      }
    }
  }, [queryData])

  const handleViewDocumentEmailsLog = () => {
    setOpenDocumentEmails(true)
  }

  const detaisInitialValue = useMemo((): DocumentDetailsSchema | undefined => {
    if (queryData.mode === 'READ_ONLY') {
      const temp = queryData.document.documentDetails.map((d) => ({
        amount: d.amount,
        date: d.date,
        description: d.description,
        quantity: d.quantity,
        reference: {
          accountantCategoryId: d.reference.accountantCategoryId ?? undefined,
          chargeCategoryId: d.reference.chargeCategoryId ?? undefined,
        },
        referenceDocumentId: d.referenceDocumentId ?? undefined,
        vatId: d.vat?.id ?? undefined,
        billId: d.billId ?? undefined,
      }))
      return temp
    }
  }, [queryData])

  useEffect(() => {
    if (Object.keys(errors).length) console.error(errors)
  }, [errors])

  const partialTsPayInfo = useMemo((): Omit<TsPayInfo, 'onPaymentConfirmed'> | undefined => {
    if (queryData.mode === 'CREATE' || queryData.mode === 'UPDATE') {
      const paymentDescription = queryData?.reservations?.length > 0 ? queryData.reservations.reduce((prev, current) => `${prev}${current.reservationCode} `, '') : watch('customer.name') || ''
      const reservationId = queryData?.reservations?.length === 1 ? queryData.reservations[0].id : undefined
      const customerEmail = watch('customer.email')
      const customerId = watch('customer.id')
      const cashDeskId = watch('cashDeskId')
      const cashDeskTypeId = cashDesksOpened?.find((c) => c.id === cashDeskId)?.cashDeskType.id ?? undefined
      if (paymentDescription && customerEmail && customerId && cashDeskTypeId) {
        return {
          customerEmail,
          paymentDescription,
          reservationId,
          customerId,
          cashDeskTypeId,
        }
      }
    }
  }, [propertyId, watch('customer.email'), watch('customer.id'), watch('customer.name'), watch('cashDeskId'), queryData, cashDesksOpened])
  //#endregion

  const canSeeZeroReceipt =
    formValue.fiscalType === 'RECEIPT' &&
    ((queryData.mode === 'CREATE' && (!queryData.bills.length || queryData.bills.some((bill) => bill.productions?.some((production) => !production.production.chargeCategory.stayIncluded)))) ||
      (queryData.mode === 'UPDATE' &&
        !queryData.document?.bills?.length &&
        queryData.document?.bills?.some((bill) => bill.productions?.some((production) => !production.production.chargeCategory.stayIncluded)))) &&
    queryData.reservations.length === 1 &&
    queryData.reservations[0].credit &&
    queryData.reservations[0].credit !== 'NC' &&
    (queryData.reservations[0].reference?.agency?.id || queryData.reservations[0].reference?.customerCompany?.id)

  const companyReferenceCustomers = referenceCustomers?.filter((c) => c.type === 'COMPANY') ?? []

  const personReferenceCustomers = referenceCustomers?.filter((customer) => customer.type === 'PERSON') ?? []

  useEffect(() => {
    if (canSeeZeroReceipt && companyReferenceCustomers?.length === 1) {
      setValue('companyToAddZeroReceipt', companyReferenceCustomers[0].name)
    }
  }, [canSeeZeroReceipt, companyReferenceCustomers.length])

  return (
    <FormLayoutDrawer //
      open={open}
      onClose={handleClose}
      title={title}
      titleOnLeft
      rightNodes={[documentDate]}
      tight
      toolbar={
        <LayoutToolbar //
          tenantId={tenantId}
          propertyId={propertyId}
          documentType={readOnlyDocument?.documentType}
          queryData={queryData}
          handleSubmit={handleSubmit}
          onClose={handleClose}
          handleDelete={handleDelete}
          loading={loading || upsertLoading}
          handleUndo={handleUndo}
          handleCreateCreditNote={handleCreateCreditNote}
          creditNoteExists={Boolean(readOnlyDocument?.creditNote)}
          onPrint={() => {
            setOpenDocumentViewer(true)
          }}
          tsPayPaymentActiveIds={tsPayPaymentActiveIds}
          handleSendPaidInFullEmail={handleSendPaidInFullEmail}
          handleSendPaidInFullMessage={handleSendPaidInFullMessage}
          documentEmailsLog={queryData.mode === 'READ_ONLY' && queryData.document.customerId ? true : false}
          handleViewDocumentEmailsLog={handleViewDocumentEmailsLog}
          allowSaveOnFinalizedDocument={allowChangesOnFinalizedDocument && (detailsIsTouched || customerIsTouched)}
          onDocumentSendByEmail={() => {
            setOpenSendDocumentByEmail(true)
          }}
          allowSavePaymentDetails={paymentsIsTouched}
          // handleSaveOnFinalizedDocument={handleSubmit}
          // handleSaveOnFinalizedDocument={updateFinalizedDocument}
        />
      }
    >
      {/* DEBUG: */}
      {/* {JSON.stringify(getValues())} */}

      {(queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE') && queryData.document.documentType === 'ADVANCE' && (
        <Tabs onChange={(_, v) => setActiveTab(v)} value={activeTab} {...{ size: 'small', textColor: 'secondary' }}>
          <Tab
            sx={{ minHeight: '48px', height: '48px' }}
            label={<Typography fontSize={'14px'}>{t('pms.common.deposit_document.document_tab_title')}</Typography>}
            iconPosition="start"
            id={`sinapsi-tab-tools-boxes`}
            aria-controls={`sinapsi-tabpanel-tools-boxes`}
          />
          <Tab
            sx={{ minHeight: '48px', height: '48px' }}
            label={<Typography fontSize={'14px'}>{t('pms.common.deposit_document.split_tab_title')}</Typography>}
            iconPosition="start"
            id={`sinapsi-tab-tools-boxes`}
            aria-controls={`sinapsi-tabpanel-tools-boxes`}
          />
        </Tabs>
      )}
      {(queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE') && openDocumentViewer && (
        <DocumentReportViewer
          open={openDocumentViewer}
          setClose={() => setOpenDocumentViewer(false)}
          documentId={queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE' ? queryData.document?.id : ''}
          propertyId={propertyId}
        />
      )}
      {(queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE') && openSendDocumentByEmail && (
        <SendCommunicationEmail
          open={openSendDocumentByEmail}
          onClose={() => {
            setOpenSendDocumentByEmail(false)
          }}
          onSendSuccess={() => {
            setOpenSendDocumentByEmail(false)
          }}
          documentId={queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE' ? queryData.document?.id : ''}
          guestEmail={queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE' ? queryData.document.customer.mailContacts?.find((mail) => mail.type === 'MAIN')?.value ?? undefined : undefined}
          subject={queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE' ? t('pms.send_communication_email.object_document', { numberExtended: queryData.document.numberExtended }) : undefined}
          propertyId={propertyId}
          tenantId={tenantId}
        />
      )}

      {/* DOCUMENT TAB CONTENT ***DEFAULT*** */}
      {activeTab === 0 && (
        <LayoutLoadingError //
          data={queryData}
          children={(coData) => (
            <Grid container spacing={1} sx={{ pt: 1 }}>
              {readOnlyDocument?.creditNote && <CoCreditNoteHeader {...readOnlyDocument.creditNote} />}
              {readOnlyDocument?.referenceDocument && (
                <CoReferenceDocumentHeader
                  {...readOnlyDocument.referenceDocument}
                  openReferenceDocument={() => {
                    if (readOnlyDocument.referenceDocument?.id && onOpenReferenceDocument) onOpenReferenceDocument(readOnlyDocument.referenceDocument.id)
                  }}
                />
              )}

              <Controller<CheckoutDocumentFormSchema, 'amounts'> //
                name="amounts"
                control={control}
                defaultValue={{
                  billsAmount: 0,
                  fiscalDepositsAmount: 0,
                  totalAmount: 0,
                  notFiscalDepositsAmount: 0,
                  paymentAmount: 0,
                }}
                render={({ field }) => (
                  <CoAmounts //
                    onChange={field.onChange}
                    queryData={coData}
                    formDeposits={formValue.deposits}
                    error={errors.amounts}
                  />
                )}
              />

              <Controller<CheckoutDocumentFormSchema, 'fiscalType'> //
                name="fiscalType"
                control={control}
                render={({ field }) => (
                  <Grid item xs={6} sm={4} lg={3}>
                    <FormItem //
                      fullWidth
                      fieldLabel={t('pms.booking.reservation.create_deposit.fiscal_type')}
                      errorMessage={errors?.fiscalType?.message}
                    >
                      <CheckoutFiscalTypeInput //
                        value={field.value || null}
                        onChange={field.onChange}
                        disabled={readonlyFiscalType || createReceiptZero}
                        error={errors.fiscalType ? true : false}
                      />
                    </FormItem>
                  </Grid>
                )}
              />

              <Controller<CheckoutDocumentFormSchema, 'cashDeskId'> //
                name="cashDeskId"
                control={control}
                render={({ field }) => (
                  <Grid item xs={6} sm={8} lg={9}>
                    <CashDeskSimpleSelector //
                      value={field.value}
                      tenantId={tenantId}
                      propertyId={propertyId}
                      fiscalType={formValue.fiscalType}
                      documentResourceTypes={documentResourceTypes}
                      onCashDeskIdChanged={field.onChange}
                      error={errors.cashDeskId?.message}
                      alertWrapper={(children) => (
                        <Grid item xs={12}>
                          {children}
                        </Grid>
                      )}
                      loading={loading}
                      disabled={coData.mode === 'READ_ONLY'}
                    />
                  </Grid>
                )}
              />

              <Grid item xs={12}>
                <Divider light />
              </Grid>

              <Controller<CheckoutDocumentFormSchema, 'customer'> //
                name="customer"
                control={control}
                defaultValue={{
                  id: '',
                  countryCode: '',
                  address: '',
                  splitPayment: false,
                }}
                render={({ field: { value, onChange } }) => (
                  <CoCustomer //
                    queryData={coData}
                    value={value}
                    customerCurrentData={customerCurrentData ?? null}
                    onChange={onChange}
                    error={errors.customer}
                    allowEditCustomer={allowChangesOnFinalizedDocument && !paymentsIsTouched}
                    referenceCustomers={createReceiptZero ? personReferenceCustomers : referenceCustomers}
                    syncData={syncData}
                    setSyncData={setSyncData}
                    setIsTouched={setCustomerAsTouched}
                    createReceiptZero={createReceiptZero}
                  />
                )}
              />

              {((coData.mode === 'CREATE' && coData.bills.length > 0) || ((coData.mode === 'UPDATE' || coData.mode === 'READ_ONLY') && Boolean(coData.document?.bills?.length))) && (
                <>
                  {(coData.mode === 'UPDATE' || coData.mode === 'READ_ONLY' || coData.mode === 'CREATE') && ( //
                    <Controller<CheckoutDocumentFormSchema, 'details'> //
                      name="details"
                      control={control}
                      defaultValue={detaisInitialValue}
                      render={({ field }) => <CoDetails data={coData} onChange={field.onChange} isTouched={setDetailsAsTouched} />}
                    />
                  )}
                  {coData.mode === 'CREATE' && (
                    <Controller<CheckoutDocumentFormSchema, 'groupBy'> //
                      name="groupBy"
                      control={control}
                      render={({ field }) => (
                        <Grid item xs={12}>
                          <FormItem //
                            fullWidth
                            fieldLabel={t('pms.common.checkout_document.group_by')}
                            errorMessage={errors?.groupBy?.message}
                          >
                            <Select
                              {...field}
                              value={field.value ?? 'ANALITIC'}
                              onChange={(event) => {
                                field.onChange(event)
                                window.localStorage.setItem('createDocumentGroupBy', event.target.value as string)
                              }}
                              disabled={loading}
                              sx={{ width: '200px' }}
                            >
                              <MenuItem value={CheckoutDocumentGroupType.ANALITIC}>{t('pms.common.checkout_document.group_by.analitic')}</MenuItem>
                              <MenuItem value={CheckoutDocumentGroupType.TOTAL}>{t('pms.common.checkout_document.group_by.total')}</MenuItem>
                              <MenuItem value={CheckoutDocumentGroupType.DATE}>{t('pms.common.checkout_document.group_by.data')}</MenuItem>
                              <MenuItem value={CheckoutDocumentGroupType.TOTAL_BILL}>{t('pms.common.checkout_document.group_by.total_bill')}</MenuItem>
                              <MenuItem value={CheckoutDocumentGroupType.DATE_BILL}>{t('pms.common.checkout_document.group_by.data_bill')}</MenuItem>
                            </Select>
                          </FormItem>
                        </Grid>
                      )}
                    />
                  )}
                  <Controller<CheckoutDocumentFormSchema, 'deposits'> //
                    name="deposits"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <CoDeposits //
                        onChange={field.onChange}
                        queryData={coData}
                        error={errors.amounts}
                        disabled={dataForGatewayPayment?.blockAmountChanges}
                      />
                    )}
                  />

                  <Controller<CheckoutDocumentFormSchema, 'payments'> //
                    name="payments"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <PaymentsFormField //
                        paymentActives={paymentActives}
                        fiscalType={watch('fiscalType')}
                        amount={formValue?.amounts?.paymentAmount}
                        value={field.value.map((row) => {
                          return {
                            ...row,
                            paymentActiveId: row.paymentActiveId ?? null,
                          }
                        })}
                        onChange={(value) => {
                          // if (field.value.length) { //FIXME: così non va bene perchè non funziona sui nuovi documenti, da sistemare!!!
                          if (coData.mode === 'READ_ONLY') setPaymenIsTouched(true)
                          field.onChange(value)
                          // }
                        }}
                        error={errors.payments}
                        readonly={coData.mode === 'READ_ONLY' || (coData.mode === 'UPDATE' && coData.document.documentPaymentDetails.some((pa) => pa.paymentActive.paymentType.sign === 'NEGATIVE'))}
                        loading={loading}
                        dataForGatewayPayment={(amount, paymentActivesWithGateway, blockAmountChanges) => {
                          setDataForGatewayPayment({ amount, paymentActivesWithGateway, blockAmountChanges })
                        }}
                        allowPaymentMethodsChanges={coData.mode === 'READ_ONLY' && !detailsIsTouched && !customerIsTouched}
                      />
                    )}
                  />
                </>
              )}
              {canSeeZeroReceipt && (
                <>
                  <Controller<CheckoutDocumentFormSchema, 'addNoteToZeroReceipt'> //
                    name="addNoteToZeroReceipt"
                    control={control}
                    render={({ field }) => (
                      <Grid item xs={12} md={8}>
                        <FormItem //
                          fullWidth
                          errorMessage={errors?.addNoteToZeroReceipt?.message}
                          disabled={createReceiptZero}
                        >
                          <FormControlLabel control={<Checkbox checked={field.value ?? false} onChange={field.onChange} />} label={t('pms.common.checkout_document.add_note_to_zero_receipt')} />
                        </FormItem>
                      </Grid>
                    )}
                  />
                  <Controller<CheckoutDocumentFormSchema, 'companyToAddZeroReceipt'> //
                    name="companyToAddZeroReceipt"
                    control={control}
                    render={({ field }) => (
                      <Grid item xs={12} md={4}>
                        <FormItem //
                          fullWidth
                          errorMessage={errors?.companyToAddZeroReceipt?.message}
                        >
                          <Select value={field.value ?? ''} onChange={field.onChange} disabled={loading || !formValue.addNoteToZeroReceipt}>
                            <MenuItem value={''}></MenuItem>
                            {companyReferenceCustomers.map((company) => (
                              <MenuItem key={company.id} value={company.name}>
                                {company.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormItem>
                      </Grid>
                    )}
                  />
                </>
              )}
              {(formValue.fiscalType === 'INVOICE' || formValue.fiscalType === 'RECEIPT') && (
                <Controller<CheckoutDocumentFormSchema, 'note'> //
                  name="note"
                  control={control}
                  render={({ field }) => (
                    <Grid item xs={12} md={6}>
                      <FormItem //
                        fullWidth
                        fieldLabel={t('pms.common.checkout_document.note')}
                        errorMessage={errors?.note?.message}
                      >
                        <TextField {...field} multiline rows={3} fullWidth disabled={loading} readOnly={coData.mode === 'READ_ONLY'} />
                      </FormItem>
                    </Grid>
                  )}
                />
              )}
              {coData.mode !== 'READ_ONLY' && partialTsPayInfo && dataForGatewayPayment ? (
                <PayByGateway
                  dataForGatewayPayment={dataForGatewayPayment}
                  propertyId={propertyId}
                  tsPayInfo={{
                    ...partialTsPayInfo,
                    onPaymentConfirmed: (success, paymentActiveId) => {
                      if (success && dataForGatewayPayment.amount) {
                        const prevPayments = watch('payments') ?? []
                        const updPayments = [{ uuid: uuidv4(), amount: dataForGatewayPayment.amount ?? 0, paymentActiveId, editable: false }, ...prevPayments.filter((x) => x.paymentActiveId)]
                        setValue('payments', updPayments)
                      }
                    },
                  }}
                  onLoading={setLoading}
                />
              ) : undefined}
              {coData.mode === 'READ_ONLY' && formValue.customer && coData.document.documentPaymentDetails.filter((dpd) => dpd.paymentActive.paymentType.code === 'UNPAID')?.length ? (
                <PaidInFullDocumentsGrid //
                  documentId={coData.document.id}
                  propertyId={propertyId}
                  cashDeskId={getValues('cashDeskId')}
                  paymentActives={paymentActives}
                  customer={formValue.customer}
                  referenceCustomer={coData.document.customer}
                />
              ) : undefined}
              {(queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE') && queryData.document.customerId && queryData.document.id && (
                <DocumentEmailsForm //
                  tenantId={tenantId}
                  open={openDocumentEmails}
                  customerId={queryData.document.customerId}
                  documentId={queryData.document.id}
                  onClose={() => setOpenDocumentEmails(false)}
                />
              )}
              {loading || (upsertLoading && <HICLoading />)}
            </Grid>
          )}
        />
      )}

      {/* DEPOSIT SPLIT TAB CONTENT */}
      {activeTab === 1 && (
        <Box sx={{ pt: 1 }}>
          {(queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE') && queryData.document.depositAssigneds && (
            <DepositSplitDataWrapper //
              propertyId={propertyId}
              data={queryData.document.depositAssigneds}
              documentId={queryData.document.id}
              loading={loading}
            />
          )}
        </Box>
      )}
    </FormLayoutDrawer>
  )
}
